import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ForumOutlinedIcon from '@material-ui/icons/ForumOutlined';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import api from '../../../../services/api';
import { ROUTES } from '../../../../constants';

// Aba "Motoristas" do Painel de Gestão (Onda 1 · aprovada).
// Ranking + ficha com ação direta na Central de Mensagens.

interface IDriverRow {
  document: string;
  name: string;
  online: boolean;
  delivered: number;
  failed: number;
  deliveriesPerDay: number;
  failedPct: number;
  avgStopMinutes: number | null;
  punctualityPct: number | null;
  topFailReason: string | null;
  trend: { date: string; count: number }[];
}

const chipStyle = (pct: number | null) => {
  if (pct === null) return { background: '#EDF1F5', color: '#5A6B7A' };
  if (pct >= 90) return { background: '#EAF5E8', color: '#2E7D32' };
  if (pct >= 80) return { background: '#FBF0DC', color: '#9A6A0B' };
  return { background: '#FAE4E4', color: '#C43D3D' };
};

const Sparkline = ({ trend }: { trend: IDriverRow['trend'] }) => {
  if (trend.length < 2) return <span style={{ color: '#999' }}>—</span>;
  const max = Math.max(...trend.map((t) => t.count), 1);
  const points = trend
    .map((t, i) => {
      const x = 2 + (i * 68) / (trend.length - 1);
      const y = 17 - (t.count / max) * 14;
      return `${x},${y}`;
    })
    .join(' ');
  const last = points.split(' ').pop()!.split(',');
  return (
    <svg width="72" height="20" viewBox="0 0 72 20" aria-hidden="true">
      <polyline
        points={points}
        fill="none"
        stroke="#0E52A0"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <circle cx={last[0]} cy={last[1]} r="2.6" fill="#0E52A0" />
    </svg>
  );
};

export const DriverScorecardTab = ({ period }: { period: string }) => {
  const [drivers, setDrivers] = useState<IDriverRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const history = useHistory();
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await api.get(
        `/summarizers/driver-scorecard?period=${period}`,
      );
      setDrivers(response.data?.drivers || []);
    } catch {
      setDrivers([]);
    }
  }, [period]);

  useEffect(() => {
    setDrivers(null);
    load();
    timer.current = setInterval(load, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  const openChat = (document: string) => {
    // a Central lê e abre direto a conversa deste motorista
    localStorage.setItem('chatOpenDriver', document);
    history.push(ROUTES.messagesCenter);
  };

  if (!drivers) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  const current =
    drivers.find((d) => d.document === selected) || drivers[0] || null;

  return (
    <Box display="flex" style={{ gap: 16 }} flexWrap="wrap">
      <Paper style={{ flex: '2 1 560px', padding: 8, overflowX: 'auto' }}>
        <Table size="small" aria-label="Ranking de motoristas">
          <TableHead>
            <TableRow>
              <TableCell>Motorista</TableCell>
              <TableCell align="right">Entregas</TableCell>
              <TableCell align="right">Entregas/dia</TableCell>
              <TableCell align="right">Insucesso</TableCell>
              <TableCell align="right">Tempo/parada</TableCell>
              <TableCell align="right">
                Pontualidade
                <Tooltip
                  title="Entregou dentro do dia previsto (deliveryPrevisionDate). ≥90% verde · 80–89% âmbar · <80% vermelho."
                  arrow
                >
                  <InfoOutlinedIcon
                    style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle', color: '#999' }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell>Tendência</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {drivers.map((d) => (
              <TableRow
                key={d.document}
                hover
                selected={current?.document === d.document}
                onClick={() => setSelected(d.document)}
                style={{ cursor: 'pointer' }}
              >
                <TableCell style={{ fontWeight: 600 }}>
                  <FiberManualRecordIcon
                    style={{
                      fontSize: 10,
                      marginRight: 6,
                      color: d.online ? '#48AE3D' : '#ccc',
                    }}
                  />
                  {d.name}
                </TableCell>
                <TableCell align="right">{d.delivered}</TableCell>
                <TableCell align="right">{d.deliveriesPerDay}</TableCell>
                <TableCell align="right">
                  {d.failedPct.toLocaleString('pt-BR')}%
                </TableCell>
                <TableCell align="right">
                  {d.avgStopMinutes === null ? '—' : `${d.avgStopMinutes} min`}
                </TableCell>
                <TableCell align="right">
                  <span
                    style={{
                      ...chipStyle(d.punctualityPct),
                      fontSize: 11,
                      fontWeight: 700,
                      padding: '2px 9px',
                      borderRadius: 999,
                    }}
                  >
                    {d.punctualityPct === null ? '—' : `${d.punctualityPct}%`}
                  </span>
                </TableCell>
                <TableCell>
                  <Sparkline trend={d.trend} />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      openChat(d.document);
                    }}
                    aria-label={`Mensagem para ${d.name}`}
                  >
                    <ForumOutlinedIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {drivers.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="textSecondary">
                    Sem atividade de motoristas no período.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {current && (
        <Paper style={{ flex: '1 1 280px', padding: 20, alignSelf: 'flex-start' }}>
          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
            {current.name}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            <FiberManualRecordIcon
              style={{
                fontSize: 10,
                marginRight: 4,
                color: current.online ? '#48AE3D' : '#ccc',
                verticalAlign: 'middle',
              }}
            />
            {current.online ? 'Online agora' : 'Offline'}
          </Typography>
          <Box mt={2}>
            {[
              ['Entregas no período', `${current.delivered}`],
              ['Insucessos', `${current.failed} (${current.failedPct.toLocaleString('pt-BR')}%)`],
              [
                'Tempo médio por parada',
                current.avgStopMinutes === null ? '—' : `${current.avgStopMinutes} min`,
              ],
              [
                'Pontualidade vs. previsão',
                current.punctualityPct === null ? '—' : `${current.punctualityPct}%`,
              ],
              ['Motivo de insucesso nº 1', current.topFailReason || '—'],
            ].map(([label, value]) => (
              <Box
                key={label}
                display="flex"
                justifyContent="space-between"
                py={0.7}
                style={{ borderBottom: '1px dashed #e0e4e8', fontSize: 13 }}
              >
                <span style={{ color: '#5A6B7A' }}>{label}</span>
                <b>{value}</b>
              </Box>
            ))}
          </Box>
          <Box display="flex" style={{ gap: 8 }} mt={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="small"
              startIcon={<ForumOutlinedIcon />}
              onClick={() => openChat(current.document)}
            >
              Mensagem
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
